<template>
  <Transition name="fly" mode="out-in">
    <button
      v-if="showButton"
      type="button"
      @click="scrollToTop"
      class="scroll-to-top-btn"
      aria-label="Scroll to top"
    >
      <svg
        class="wave-shape"
        viewBox="0 0 140 64"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M0 64C20 64 31 57 44 46C56 35 62 22 70 22C78 22 84 35 96 46C109 57 120 64 140 64V64H0Z"
        />
      </svg>
      <svg
        class="scroll-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 14L12 9L17 14"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const showButton = ref(false);
const scrollThreshold = 100; // Show button after scrolling 100px (reduced for testing)

const handleScroll = () => {
  showButton.value = window.scrollY > scrollThreshold;
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.scroll-to-top-btn {
  position: fixed !important;
  right: clamp(0.75rem, 2vw, 1.5rem) !important;
  bottom: 0 !important;
  width: 4.6rem !important;
  height: 0.85rem !important;
  border: 0 !important;
  background: transparent !important;
  color: #ffffff !important;
  z-index: 1000 !important;
  display: flex !important;
  align-items: flex-end !important;
  justify-content: center !important;
  padding: 0 !important;
  cursor: pointer;
  overflow: visible;
  transform-origin: bottom center;
  transition:
    width 220ms ease,
    height 220ms ease;
}

.wave-shape {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  color: #00047b;
  filter: drop-shadow(0 -1px 5px rgba(0, 4, 123, 0.18));
  pointer-events: none;
  transition:
    filter 220ms ease,
    transform 220ms ease;
}

.wave-shape path {
  fill: currentColor;
}

.scroll-to-top-btn:hover,
.scroll-to-top-btn:focus-visible {
  width: 6.2rem !important;
  height: 2.25rem !important;
}

.scroll-to-top-btn:hover .wave-shape,
.scroll-to-top-btn:focus-visible .wave-shape {
  filter: drop-shadow(0 -3px 10px rgba(0, 4, 123, 0.22));
  transform: scaleX(1.03);
}

.scroll-to-top-btn:focus-visible {
  outline: 2px solid rgba(0, 4, 123, 0.28);
  outline-offset: 4px;
}

.scroll-icon {
  position: absolute;
  left: 50%;
  top: 62%;
  z-index: 1;
  width: 1.15rem !important;
  height: 1.15rem !important;
  flex-shrink: 0;
  opacity: 0;
  transform: translate(-50%, -20%);
  transition:
    opacity 160ms ease 80ms,
    transform 160ms ease 80ms;
}

.scroll-to-top-btn:hover .scroll-icon,
.scroll-to-top-btn:focus-visible .scroll-icon {
  opacity: 1;
  transform: translate(-50%, -62%);
}

/* Fly-in from bottom / Fly-out to bottom animations */
.fly-enter-active {
  transition:
    opacity 0.28s ease,
    transform 0.28s ease;
}

.fly-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fly-enter-from {
  opacity: 0;
  transform: translateY(1.5rem);
}

.fly-leave-to {
  opacity: 0;
  transform: translateY(1.5rem);
}

.fly-enter-to,
.fly-leave-from {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .scroll-to-top-btn,
  .wave-shape,
  .scroll-icon,
  .fly-enter-active,
  .fly-leave-active {
    transition: none;
  }
}
</style>
